import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { MapPin, Clock, Navigation, Info, CalendarDays } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import './LocationContact.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CENTER = [19.9449322, -103.7591662];

export const LocationContact = () => {
  const [visible, setVisible] = useState(false);
  const [horarios, setHorarios] = useState([]);
  const [notaEspecial, setNotaEspecial] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchHorarios = async () => {
      try {
        const res = await fetch(`${API_URL}/api/horarios`);
        const data = await res.json();
        // Filtrar solo los días abiertos
        const openDays = (data.horarios || []).filter(h => h.is_open);
        setHorarios(openDays);
        setNotaEspecial(data.nota_especial || '');
      } catch (error) {
        console.error('Error fetching horarios:', error);
      }
    };
    fetchHorarios();
  }, []);

  const handleDirections = () => {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${CENTER[0]},${CENTER[1]}`, '_blank');
  };

  // Determinar si es fin de semana
  const isWeekend = horarios.every(h => 
    ['Viernes', 'Sábado', 'Domingo'].includes(h.day)
  );

  return (
    <section className="location" id="ubicacion" ref={ref} data-testid="location-section">
      <div className="location-container">
        <div className={`location-header ${visible ? 'vis' : ''}`}>
          <div className="section-fire-line" />
          <span className="location-eyebrow">Encuéntranos</span>
          <h2 className="location-title">Ven a Visitarnos</h2>
        </div>

        <div className="location-grid">
          {/* Info column */}
          <div className={`location-info ${visible ? 'vis' : ''}`}>
            {/* Address card */}
            <div className="location-card" data-testid="map-address">
              <div className="location-card-icon"><MapPin size={20} /></div>
              <div>
                <h3>Dirección</h3>
                <p><strong>Andador Álvaro Obregón #110</strong></p>
                <p>Colonia Centro, Tapalpa</p>
                <p>Jalisco, México</p>
                <p className="location-ref">Frente a los Jaimes</p>
              </div>
            </div>

            {/* Schedule card */}
            <div className="location-card" data-testid="schedule-section">
              <div className="location-card-icon"><CalendarDays size={20} /></div>
              <div>
                <h3>Horarios {isWeekend ? 'Fin de Semana' : ''}</h3>
                <div className="location-schedule" data-testid="schedule-rows">
                  {horarios.map((s, i) => (
                    <div className="location-schedule-row" key={i} data-testid={`schedule-row-${i}`}>
                      <Clock size={14} />
                      <span className="location-day">{s.day}</span>
                      <span className="location-hours">{s.hours}</span>
                    </div>
                  ))}
                </div>
                {notaEspecial && (
                  <div className="location-note" data-testid="schedule-note">
                    <Info size={14} />
                    <span dangerouslySetInnerHTML={{ __html: notaEspecial.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                  </div>
                )}
              </div>
            </div>

            <button className="location-directions" onClick={handleDirections} data-testid="directions-btn">
              <Navigation size={16} />
              Cómo llegar
            </button>
          </div>

          {/* Map column */}
          <div className={`location-map ${visible ? 'vis' : ''}`} data-testid="map-leaflet">
            <MapContainer
              center={CENTER}
              zoom={17}
              scrollWheelZoom={false}
              style={{ width: '100%', height: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={CENTER}>
                <Popup>
                  <strong>Dogos El Tío Chepe</strong><br />
                  Andador Álvaro Obregón #110<br />
                  Tapalpa, Jalisco
                </Popup>
              </Marker>
            </MapContainer>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LocationContact;
